import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useMemo, useState } from "react";

export default function WordList({
    filledCoordinates,
    selectedWord,
    onWordSelect,
    showVisibilityToggle = false,
    onVisibilityToggle,
    showAllWords = false,
    maxHeight = 400,
    onDeleteWord = null
}) {
    const [localShowAllWords, setLocalShowAllWords] = useState(false);

    const handleVisibilityToggle = () => {
        const newValue = !localShowAllWords;
        setLocalShowAllWords(newValue);
        if (onVisibilityToggle) {
            onVisibilityToggle(newValue);
        }
    };

    const isVisible = showVisibilityToggle ? localShowAllWords : showAllWords;

    // Process filledCoordinates: show reading word (canonical spelling). Prefer item.word when present; else item.text as-is (do not reverse for display).
    const processedWords = useMemo(() => {
        const result = filledCoordinates ? filledCoordinates
            .filter(item => item.text || item.word)
            .map((item) => {
                const raw = item.text ?? item.word ?? '';
                const displayText = (item.word != null && String(item.word).trim() !== '')
                    ? String(item.word).trim()
                    : raw;
                return {
                    id: item.id,
                    text: item.text,
                    word: item.word,
                    displayText,
                    reversed: item.reversed,
                    direction: item.direction,
                    length: item.length,
                    positions: item.positions
                };
            })
            .sort((a, b) => a.displayText.localeCompare(b.displayText)) : [];
        return result;
    }, [filledCoordinates]);

    return (
        <Paper elevation={2} sx={{ 
            p: 2, 
            height: 'fit-content', 
            width: '100%', 
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                    Words ({processedWords.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    {onDeleteWord && selectedWord && (
                        <Tooltip title="Delete selected word from puzzle">
                            <IconButton
                                onClick={onDeleteWord}
                                size="small"
                                sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                        backgroundColor: 'error.light'
                                    }
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {showVisibilityToggle && (
                        <Tooltip title={isVisible ? "Hide all words" : "Show all words"}>
                            <IconButton
                                onClick={handleVisibilityToggle}
                                size="small"
                                sx={{
                                    color: isVisible ? 'primary.main' : 'text.secondary',
                                    backgroundColor: isVisible ? 'primary.light' : 'transparent'
                                }}
                            >
                                {isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: maxHeight,
                overflowY: "auto",
                overflowX: "hidden",
                alignItems: "flex-start",
                alignContent: "flex-start",
                width: "100%",
                boxSizing: 'border-box'
            }}>
                {processedWords.map((wordItem, index) => {
                    const displayText = wordItem.displayText ?? wordItem.text;
                    const labelText = (displayText ?? "").toLowerCase();
                    const isSelected = selectedWord != null && displayText.toLowerCase() === selectedWord.toLowerCase();
                    return (
                        <Chip
                            key={`word-${wordItem.id || wordItem.text || index}`}
                            label={labelText}
                            variant="outlined"
                            size="small"
                            onClick={() => onWordSelect(displayText)}
                            sx={{
                                cursor: "pointer",
                                maxWidth: "100%",
                                backgroundColor: isSelected
                                    ? "primary.main"
                                    : wordItem.direction === 'scattered'
                                        ? "#fff59d" // MUI yellow 200
                                        : "transparent",
                                color: isSelected
                                    ? "white"
                                    : wordItem.direction === 'scattered'
                                        ? "#5d4037" // brown 700 for contrast
                                        : "inherit",
                                borderColor: isSelected
                                    ? "primary.main"
                                    : wordItem.direction === 'scattered'
                                        ? "#000000" // black border
                                        : "inherit",
                                borderWidth: "1px",
                                borderStyle: "solid",
                                "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "200px"
                                },
                                "&:hover": {
                                    backgroundColor: isSelected
                                        ? "primary.dark"
                                        : wordItem.direction === 'scattered'
                                            ? "#fff176" // yellow 300 on hover
                                            : "action.hover"
                                }
                            }}
                        />
                    );
                })}
            </Box>
        </Paper>
    );
}
